import React, { useState, useEffect, useRef } from "react";
import { useTasks } from "../../context/TaskContext";
import { useProjects } from "../../context/ProjectContext";
import { toast } from "sonner";
import { getLocalDateString } from "../../utils/dateUtils";

import { SchedulePicker } from "./selectors/SchedulePicker";
import { PriorityPicker } from "./selectors/PriorityPicker";
import { ReminderPicker } from "./selectors/ReminderPicker";
import { ProjectPicker } from "./selectors/ProjectPicker";

export const TaskEditModal = ({ isOpen, onClose, task }) => {
    const { updateTask } = useTasks();
    const { projects } = useProjects();

    const [newIndexName, setNewIndexName] = useState(task.name);
    const [newDescription, setNewDescription] = useState(task.description || "");
    const [newProjectId, setNewProjectId] = useState(task.project_id || "inbox");
    const [newPriority, setNewPriority] = useState(task.priority || 4);

    const [newDueDate, setNewDueDate] = useState(task.due_date || getLocalDateString());
    const [newDueTime, setNewDueTime] = useState(task.due_time || "");
    const [newIsRecurring, setNewIsRecurring] = useState(task.is_recurring || false);
    const [newRecurrenceType, setNewRecurrenceType] = useState(task.recurrence_type || "daily");
    const [newRecurrenceDays, setNewRecurrenceDays] = useState(task.recurrence_days || []);

    const [newReminderType, setNewReminderType] = useState(task.reminder_type || "none");
    const [newReminderDate, setNewReminderDate] = useState(task.reminder_date || "");

    // Estado para prevenir doble clic
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Estados de UI para desplegables
    const [showSchedule, setShowSchedule] = useState(false);
    const [showPriority, setShowPriority] = useState(false);
    const [showReminder, setShowReminder] = useState(false);

    const scheduleRef = useRef(null);
    const priorityRef = useRef(null);
    const reminderRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (scheduleRef.current && !scheduleRef.current.contains(event.target)) setShowSchedule(false);
            if (priorityRef.current && !priorityRef.current.contains(event.target)) setShowPriority(false);
            if (reminderRef.current && !reminderRef.current.contains(event.target)) setShowReminder(false);
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSaveEdit = async (e) => {
        e.preventDefault();

        if (!newIndexName || newIndexName.trim() === "" || isSubmitting) {
            if (!newIndexName || newIndexName.trim() === "") {
                toast.error("¡El nombre de la tarea no puede estar vacío!");
            }
            return;
        }

        setIsSubmitting(true);

        try {
            const updatePayload = {
                name: newIndexName.trim(),
                description: newDescription.trim() || null,
                project_id: newProjectId === "inbox" ? null : Number(newProjectId),
                priority: newPriority,
                due_date: newDueDate,
                due_time: newDueTime || null,
                is_recurring: newIsRecurring,
                recurrence_type: newIsRecurring ? newRecurrenceType : null,
                recurrence_days: newIsRecurring && newRecurrenceType === "custom" ? newRecurrenceDays : null,
                reminder_type: newReminderType,
                reminder_date: newReminderType === "custom" ? newReminderDate : null
            };

            await updateTask(task.id, updatePayload);
            toast.success("¡Tarea modificada correctamente!");
            onClose();
        } catch (error) {
            toast.error("Error al modificar la tarea.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>

            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-lg w-full p-5 relative z-10 animate-fadeIn">
                <div className="flex items-center space-x-2 mb-4">
                    <i className="far fa-edit text-blue-500 text-base"></i>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Editar Tarea</h2>
                </div>

                <form onSubmit={handleSaveEdit} className="space-y-4">
                    <input
                        type="text"
                        value={newIndexName}
                        onChange={(e) => setNewIndexName(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 font-medium"
                        placeholder="Título de la tarea"
                        autoFocus
                    />

                    <textarea
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-600 h-20 resize-none"
                        placeholder="Añade una descripción..."
                    />

                    {/* Controles de Configuración Rápida */}
                    <div className="flex items-center space-x-2 pt-1">
                        <SchedulePicker
                            dueDate={newDueDate}
                            setDueDate={setNewDueDate}
                            dueTime={newDueTime}
                            setDueTime={setNewDueTime}
                            isRecurring={newIsRecurring}
                            setIsRecurring={setNewIsRecurring}
                            recurrenceType={newRecurrenceType}
                            setRecurrenceType={setNewRecurrenceType}
                            recurrenceDays={newRecurrenceDays}
                            setRecurrenceDays={setNewRecurrenceDays}
                            showSchedule={showSchedule}
                            setShowSchedule={setShowSchedule}
                            scheduleRef={scheduleRef}
                        />

                        <PriorityPicker
                            priority={newPriority}
                            setPriority={setNewPriority}
                            showPriority={showPriority}
                            setShowPriority={setShowPriority}
                            priorityRef={priorityRef}
                        />

                        <ReminderPicker
                            reminderType={newReminderType}
                            setReminderType={setNewReminderType}
                            reminderDate={newReminderDate}
                            setReminderDate={setNewReminderDate}
                            showReminder={showReminder}
                            setShowReminder={setShowReminder}
                            reminderRef={reminderRef}
                        />
                    </div>

                    {/* Separador con Selector de Destino */}
                    <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                        <ProjectPicker
                            projects={projects}
                            projectId={newProjectId}
                            setProjectId={setNewProjectId}
                        />

                        <div className="flex space-x-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting || !newIndexName.trim()}
                                className={`px-4 py-1.5 text-xs font-semibold text-white rounded-lg shadow-md transition-all flex items-center space-x-1.5 ${
                                    isSubmitting || !newIndexName.trim()
                                        ? "bg-blue-400 cursor-not-allowed opacity-75"
                                        : "bg-blue-600 hover:bg-blue-700 active:scale-95"
                                }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin text-xs"></i>
                                        <span>Guardando...</span>
                                    </>
                                ) : (
                                    <span>Guardar</span>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
